import React, { useEffect, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from '@shared/utils/i18n';
import type { AxiosRequestConfig } from 'axios';
import { getErrorText, notifications } from '@shared/utils/notifications';
import { axiosForReactQueryInstance, queryClient } from '@shared/api/reactQuery';
import { ApiResponse } from '@shared/api/orval/models/apiResponse';
import { AddAccountButton } from '@features/osi/accounts/ui/AddAccountButton';
import { AccountFormWithFile, OsiAccountTypes } from '@shared/types/osi/accounts';
import { ApplicationTypes, OsiAccountApplicationRequestWithFile } from '@shared/types/osiAccountApplications';
import { createOsiApplication } from '@shared/api/osiAccountApplications';
import {
  getServiceGroupApplicationsByOsiId,
  getServiceGroupApplicationsByOsiIdQueryKey
} from '@shared/api/serviceGroupApplications';
import { ServiceGroupApplication } from '@shared/types/serviceGroupApplications';

interface AvailableGroup {
  id: number;
  nameRu: string;
  nameKz: string;
  code: string;
}

// Счёт ОСИ для dropdown
interface OsiAccountItem {
  id: number;
  account: string;
  type: string;  // API GET /api/Osi/{osiId}/accounts возвращает "type", не "accountTypeCode"
  accountTypeNameRu?: string;
  bankName?: string;
}

// Категория доходов для dropdown
interface ReportCategory {
  id: number;
  number: string;
  nameRu: string;
  nameKz: string;
}

// Специальное значение для пункта "Создать произвольную группу"
const CREATE_CUSTOM_VALUE = -1;

// Коды групп капитального ремонта — для них показываем только сберегательные счета (SAVINGS)
const BIG_REPAIR_CODES = ['BIG_REPAIR', 'BIG_REPAIR_PARKING', 'BIG_REPAIR_STORAGE'];

// OSI-823: лимит названия произвольной группы услуг (RU и KZ). Колонки БД varchar(100), лимит держится на уровне приложения;
// синхронизирован с [MaxLength] в ServiceGroupRequest.cs
const GROUP_NAME_MAX_LENGTH = 70;

// OSI-822 (дефект b): AXIOS_INSTANCE за axiosForReactQueryInstance возвращает ВЕСЬ конверт ApiResponse
// и НЕ проверяет code (в отличие от instance из shared/api/config.ts). Бизнес-ошибка бэка приходит с HTTP 200
// и code != 0 — раньше она проглатывалась и пользователь видел ложный success-тост.
// Здесь: code != 0 → throw Error(message), чтобы catch показал текст бэка; при успехе возвращаем result.
// Пустой ответ (401 — интерцептор делает return undefined после «Сессия истекла») тоже считаем неуспехом,
// иначе после него показался бы success-тост.
const requestCore = async <T,>(config: AxiosRequestConfig): Promise<T | null | undefined> => {
  const response = await axiosForReactQueryInstance<ApiResponse & { result?: T | null }>(config);
  if (!response) throw new Error();
  if (response.code !== 0) throw new Error(response.message ?? '');
  return response.result;
};

interface Props {
  osiId: number;
  reloadCb: () => Promise<void>;
  /**
   * Колбэк для передачи функции openEditAccount родителю (с опциональным currentLinkedAccountId и groupCode).
   * Новые обязательные параметры: isCustom (произвольная группа = per-OSI, osi_id != null — включает редактирование имени/категории),
   * groupNameRu/groupNameKz и reportCategoryId — для предзаполнения полей редактирования произвольной группы.
   */
  onEditAccountReady?: (openEditAccount: (serviceGroupId: number, currentLinkedAccountId: number | null | undefined, groupCode: string | undefined, isCustom: boolean, groupNameRu: string, groupNameKz: string, reportCategoryId: number | null) => void) => void;
}

// Фаза 1: единая кнопка "Подключить группу" с возможностью создания произвольной
// + смена привязанного счёта на существующей группе (PUT /api/ServiceGroup/account)
export const ServiceGroupManagement: React.FC<Props> = observer(({ osiId, reloadCb, onEditAccountReady }) => {
  const { t, getLocale } = useTranslation();
  // OSI-786: роль текущего пользователя. Только ADMIN может редактировать наименование группы.
  const rolesModule = useInjection<RolesService>(RolesServiceIdentifier);
  const isAdmin = rolesModule.isHasAdminRole;

  // --- Основной диалог выбора группы ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<AvailableGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('');
  const [activateLoading, setActivateLoading] = useState(false);
  // --- Форма создания произвольной группы (второй шаг) ---
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [customNameRu, setCustomNameRu] = useState('');
  const [customNameKz, setCustomNameKz] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<number | ''>('');
  // OSI-806: категория отчёта БЕЗ предвыбранного значения (раньше молча подставлялась id=10 «6.7 Иные поступления»).
  // Тип и начальное значение — один в один как у «Расчётный счёт» выше (number | '' + ''), чтобы поля вели себя одинаково.
  // Пустое значение стало допустимым состоянием формы, поэтому выбор категории сделан ОБЯЗАТЕЛЬНЫМ (см. disabled у кнопок «Сохранить»):
  // так на бэк никогда не уйдёт дефолт 10 и в БД не появится NULL.
  const [selectedReportCategoryId, setSelectedReportCategoryId] = useState<number | ''>('');
  const [createLoading, setCreateLoading] = useState(false);

  // Справочники для формы создания
  const [osiAccounts, setOsiAccounts] = useState<OsiAccountItem[]>([]);
  const [reportCategories, setReportCategories] = useState<ReportCategory[]>([]);

  // --- Смена счёта на существующей группе ---
  const [editAccountGroupId, setEditAccountGroupId] = useState<number | null>(null);
  // Флаг: редактируемая группа произвольная (isCustom = per-OSI, osi_id != null) — показывать поля имени и категории
  const [editAccountIsCustom, setEditAccountIsCustom] = useState(false);
  const [editAccountOpen, setEditAccountOpen] = useState(false);
  // Текущий привязанный счёт — для подсветки жирным и предвыбора в dropdown
  const [currentLinkedAccountId, setCurrentLinkedAccountId] = useState<number | null>(null);

  // OSI-828: заявки ОСИ на группы больше не выводятся блоком на странице — нужны только для проверки ниже:
  // пока заявка на СОЗДАНИЕ группы (ADD) на модерации, новую произвольную группу создать нельзя.
  // Заявки RENAME не блокируют. Пока данные не загрузились, hasPendingAdd = false — подстраховывает проверка на бэке.
  const { data: groupApplications } = useQuery({
    queryKey: getServiceGroupApplicationsByOsiIdQueryKey(osiId),
    queryFn: () => getServiceGroupApplicationsByOsiId(osiId),
    enabled: osiId > 0
  });
  const hasPendingAdd = (groupApplications ?? []).some((a) => a.applicationType === 'ADD' && a.state === 'PENDING');

  // OSI-822: обновить список заявок на группы после POST/PUT, создавших заявку
  // (OSI-828: от него зависит hasPendingAdd)
  const invalidateApplications = () =>
    queryClient.invalidateQueries({ queryKey: getServiceGroupApplicationsByOsiIdQueryKey(osiId) });

  // --- Колбэк для AddAccountButton: создаёт заявку на добавление счёта через API ---
  const handleAddAccount = useCallback(async (data: AccountFormWithFile) => {
    try {
      const docs = [data.file];

      const payload: OsiAccountApplicationRequestWithFile = {
        applicationType: ApplicationTypes.ADD,
        osiId,
        type: data.accountType as OsiAccountTypes,
        bic: data.bank.bic,
        account: data.account,
        docs,
      };

      await createOsiApplication(payload);
      notifications.success(t('accruals:accountApplicationCreated'));
    } catch (e) {
      // OSI-822: instance бросает СТРОКУ (message бэка) — e?.message был undefined и текст ошибки терялся
      notifications.error(getErrorText(e));
    }
  }, [osiId, t]);

  // Открыть диалог смены счёта для конкретной группы
  // groupCode — код группы, для BIG_REPAIR_* показываем только сберегательные счета
  // isCustom — признак произвольной группы (per-OSI, osi_id != null): если true, дополнительно показываем поля имени (ru/kz) и категории дохода
  // groupNameRu/groupNameKz/reportCategoryId — текущие значения группы для предзаполнения полей редактирования
  const openEditAccount = useCallback(async (serviceGroupId: number, linkedAccountId: number | null | undefined, groupCode: string | undefined, isCustom: boolean, groupNameRu: string, groupNameKz: string, reportCategoryId: number | null) => {
    setEditAccountGroupId(serviceGroupId);
    // Запоминаем, произвольная ли группа — от этого зависит набор полей в попапе
    setEditAccountIsCustom(isCustom);
    // Сохраняем текущий привязанный счёт для подсветки
    setCurrentLinkedAccountId(linkedAccountId ?? null);
    // Для произвольных групп предзаполняем имя/категорию и догружаем справочник категорий дохода
    if (isCustom) {
      setCustomNameRu(groupNameRu);
      setCustomNameKz(groupNameKz);
      // OSI-806: показываем РЕАЛЬНУЮ категорию группы. Если у группы категории нет — поле остаётся пустым
      // (раньше подставлялась 6.7 / id=10 и уезжала на бэк при первом же сохранении).
      // Пустое значение блокирует «Сохранить», пока пользователь не выберет категорию явно.
      setSelectedReportCategoryId(reportCategoryId ?? '');
      try {
        // OSI-822: через requestCore — проверка code (дефект b)
        const categories = await requestCore<ReportCategory[]>({
          url: '/api/ServiceGroup/report-categories',
          method: 'GET'
        });
        setReportCategories(categories ?? []);
      } catch (e) {
        setReportCategories([]);
        // OSI-822: раньше ошибка глоталась молча — показываем текст
        notifications.error(getErrorText(e));
      }
    }
    try {
      // OSI-822: через requestCore — проверка code (дефект b)
      const allAccounts =
        (await requestCore<OsiAccountItem[]>({
          url: `/api/Osi/${osiId}/accounts`,
          method: 'GET'
        })) ?? [];
      // Для групп капремонта — только сберегательные счета (SAVINGS)
      const isBigRepair = groupCode && BIG_REPAIR_CODES.includes(groupCode);
      const accounts = isBigRepair
        ? allAccounts.filter(a => a.type === 'SAVINGS')  // API возвращает поле "type"
        : allAccounts;
      setOsiAccounts(accounts);
      // Предвыбираем текущий привязанный счёт, если он есть в списке
      if (linkedAccountId && accounts.some(a => a.id === linkedAccountId)) {
        setSelectedAccountId(linkedAccountId);
      } else if (accounts.length > 0) {
        setSelectedAccountId(accounts[0].id);
      } else {
        setSelectedAccountId('');
      }
    } catch (e) {
      setOsiAccounts([]);
      notifications.error(getErrorText(e));
    }
    setEditAccountOpen(true);
  }, [osiId]);

  // Сохранить изменения группы.
  // Для произвольных групп (editAccountIsCustom = per-OSI, osi_id != null) — полное обновление группы (имя ru/kz, категория, счёт).
  // Для системных/фиксированных групп — только смена привязанного счёта (прежнее поведение).
  const saveEditAccount = async () => {
    if (!editAccountGroupId || !selectedAccountId) return;
    setActivateLoading(true);
    try {
      if (editAccountIsCustom) {
        // OSI-806: категория обязательна ТОЛЬКО для произвольной группы (у фиксированных поле не показывается
        // и в запрос PUT /api/ServiceGroup/account не входит — ветка else ниже). Проверка сужает тип
        // selectedReportCategoryId с (number | '') до number, поэтому в payload не могут попасть '' или 0.
        if (!selectedReportCategoryId) return;
        // Произвольная группа: обновляем имя, категорию дохода и счёт через PUT /api/ServiceGroup/{id}
        // Тип счёта определяем из выбранного счёта (API возвращает поле "type"), fallback — CURRENT
        const accountTypeCode = osiAccounts.find(a => a.id === selectedAccountId)?.type ?? 'CURRENT';
        // OSI-822: счёт и категория применяются сразу; если имя изменилось — бэк создаёт заявку RENAME
        // (старое имя действует до одобрения) и возвращает её в result; null — имя не менялось.
        const renameApplication = await requestCore<ServiceGroupApplication>({
          url: `/api/ServiceGroup/${editAccountGroupId}`,
          method: 'PUT',
          data: {
            osiId,
            nameRu: customNameRu.trim(),
            // Если каз. имя пустое — дублируем русское (как в форме создания)
            nameKz: customNameKz.trim() || customNameRu.trim(),
            accountTypeCode,
            osiAccountId: selectedAccountId,
            reportCategoryId: selectedReportCategoryId
          }
        });
        if (renameApplication) {
          // OSI-828: единый текст «Заявка отправлена на модерацию» — как при создании группы
          notifications.success(t('accruals:groupApplicationSent'));
          await invalidateApplications();
        } else {
          notifications.success(t('accruals:groupUpdated'));
        }
        setEditAccountOpen(false);
        setEditAccountGroupId(null);
        await reloadCb();
      } else {
        // Фиксированная группа: прежнее поведение — только смена счёта
        // OSI-822: через requestCore — проверка code (дефект b)
        await requestCore({
          url: '/api/ServiceGroup/account',
          method: 'PUT',
          params: { osiId, serviceGroupId: editAccountGroupId, osiAccountId: selectedAccountId }
        });
        notifications.success(t('accruals:accountChanged'));
        setEditAccountOpen(false);
        setEditAccountGroupId(null);
        await reloadCb();
      }
    } catch (e) {
      // OSI-822: текст ошибки бэка (Error из requestCore или AxiosError) — раньше при code != 0 сюда не попадали вовсе
      notifications.error(getErrorText(e));
    } finally {
      setActivateLoading(false);
    }
  };

  // Передаём функцию openEditAccount родителю через колбэк
  useEffect(() => {
    onEditAccountReady?.(openEditAccount);
  }, [onEditAccountReady, openEditAccount]);

  // Открыть основной диалог — загрузить доступные группы
  const openDialog = async () => {
    try {
      // OSI-822: через requestCore — проверка code (дефект b)
      const groups = await requestCore<AvailableGroup[]>({
        url: `/api/ServiceGroup/available/${osiId}`,
        method: 'GET'
      });
      setAvailableGroups(groups ?? []);
    } catch (e) {
      setAvailableGroups([]);
      notifications.error(getErrorText(e));
    }
    setSelectedGroupId('');
    setDialogOpen(true);
  };

  // Кнопка в основном диалоге: сразу сохраняет (счета уже загружены автоматически)
  const handleMainAction = async () => {
    if (selectedGroupId === CREATE_CUSTOM_VALUE) {
      // OSI-828: предыдущая заявка на создание группы ещё на модерации — форму не открываем, диалог оставляем
      if (hasPendingAdd) {
        notifications.warning(t('accruals:groupPendingAddApplicationExists'));
        return;
      }
      // Переход к форме создания произвольной группы
      setDialogOpen(false);
      await openCreateForm();
      return;
    }
    if (!selectedGroupId || !selectedAccountId) return;

    setActivateLoading(true);
    try {
      // OSI-822: через requestCore — проверка code (дефект b)
      await requestCore({
        url: '/api/ServiceGroup/activate',
        method: 'POST',
        params: { osiId, serviceGroupId: selectedGroupId, osiAccountId: selectedAccountId }
      });
      notifications.success(t('accruals:groupActivated'));
      setDialogOpen(false);
      setSelectedGroupId('');
      setOsiAccounts([]);
      await reloadCb();
    } catch (e) {
      // OSI-822: текст ошибки бэка (Error из requestCore или AxiosError) — раньше при code != 0 сюда не попадали вовсе
      notifications.error(getErrorText(e));
    } finally {
      setActivateLoading(false);
    }
  };

  // --- Форма создания произвольной группы ---

  const openCreateForm = async () => {
    // Загружаем счета и категории
    try {
      // OSI-822: через requestCore — проверка code (дефект b)
      const [accountsResult, categoriesResult] = await Promise.all([
        requestCore<OsiAccountItem[]>({
          url: `/api/Osi/${osiId}/accounts`,
          method: 'GET'
        }),
        requestCore<ReportCategory[]>({
          url: '/api/ServiceGroup/report-categories',
          method: 'GET'
        })
      ]);
      const accounts = accountsResult ?? [];
      setOsiAccounts(accounts);
      // По умолчанию первый счёт
      if (accounts.length > 0) setSelectedAccountId(accounts[0].id);

      setReportCategories(categoriesResult ?? []);
    } catch (e) {
      // OSI-822: раньше ошибка игнорировалась молча — показываем текст
      notifications.error(getErrorText(e));
    }

    setCustomNameRu('');
    setCustomNameKz('');
    // OSI-806: форма создания открывается с ПУСТОЙ категорией отчёта (было setSelectedReportCategoryId(10)).
    // Сброс здесь обязателен: стейт общий с модалкой редактирования, иначе сюда протекло бы значение
    // ранее отредактированной произвольной группы.
    setSelectedReportCategoryId('');
    setCreateFormOpen(true);
  };

  const createCustomGroup = async () => {
    // OSI-806: категория отчёта обязательна. Проверка дублирует disabled кнопки и одновременно
    // сужает тип selectedReportCategoryId с (number | '') до number — '' или 0 физически не могут уйти в payload ниже.
    if (!customNameRu.trim() || !selectedAccountId || !selectedReportCategoryId) return;
    setCreateLoading(true);
    try {
      // Определяем тип счёта из выбранного счёта (API возвращает поле "type")
      const selectedAccount = osiAccounts.find(a => a.id === selectedAccountId);
      const accountTypeCode = selectedAccount?.type ?? 'CURRENT';

      // OSI-822: группа НЕ создаётся сразу — бэк создаёт заявку ADD, группа появится после одобрения в админке.
      await requestCore<ServiceGroupApplication>({
        url: '/api/ServiceGroup',
        method: 'POST',
        data: {
          osiId,
          nameRu: customNameRu.trim(),
          nameKz: customNameKz.trim() || customNameRu.trim(),
          accountTypeCode,
          osiAccountId: selectedAccountId,
          reportCategoryId: selectedReportCategoryId
        }
      });
      // OSI-822: тост о модерации вместо «Группа создана»; список групп не перезагружаем (новой группы ещё нет),
      // обновляем блок заявок.
      notifications.success(t('accruals:groupApplicationSent'));
      setCreateFormOpen(false);
      await invalidateApplications();
    } catch (e) {
      // OSI-822: текст ошибки бэка (Error из requestCore или AxiosError) — раньше при code != 0 сюда не попадали вовсе
      notifications.error(getErrorText(e));
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    // OSI-822: flexWrap — чтобы блок заявок (width 100%) переносился на отдельную строку под кнопкой;
    // диалоги рендерятся порталом и в раскладке не участвуют.
    // OSI-828: блок заявок убран, flexWrap оставлен без изменений.
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, mb: 1 }}>
      {/* Единая кнопка */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<LinkIcon />}
        onClick={openDialog}
      >
        {t('accruals:activateGroup')}
      </Button>

      {/* Диалог выбора группы — фиксированные + "Создать произвольную" + выбор счёта в том же диалоге */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('accruals:activateGroup')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label={t('accruals:selectGroup')}
            value={selectedGroupId}
            onChange={async (e) => {
              const groupId = Number(e.target.value);
              setSelectedGroupId(groupId);

              // Если выбрана фиксированная группа — загружаем счета сразу
              if (groupId !== CREATE_CUSTOM_VALUE && groupId) {
                try {
                  // OSI-822: через requestCore — проверка code (дефект b)
                  const allAccounts =
                    (await requestCore<OsiAccountItem[]>({
                      url: `/api/Osi/${osiId}/accounts`,
                      method: 'GET'
                    })) ?? [];
                  const selectedGroup = availableGroups.find(g => g.id === groupId);
                  const isBigRepair = selectedGroup && BIG_REPAIR_CODES.includes(selectedGroup.code);
                  const accounts = isBigRepair
                    ? allAccounts.filter(a => a.type === 'SAVINGS')
                    : allAccounts;
                  setOsiAccounts(accounts);
                  if (accounts.length > 0) setSelectedAccountId(accounts[0].id);
                  else setSelectedAccountId('');
                } catch (err) {
                  setOsiAccounts([]);
                  notifications.error(getErrorText(err));
                }
              } else {
                setOsiAccounts([]);
                setSelectedAccountId('');
              }
            }}
            sx={{ mt: 1 }}
          >
            {availableGroups.map((g) => (
              <MenuItem key={g.id} value={g.id}>{g.nameRu}</MenuItem>
            ))}
            {/* Последний пункт — создать произвольную группу */}
            <MenuItem value={CREATE_CUSTOM_VALUE} sx={{ fontStyle: 'italic', borderTop: '1px solid', borderColor: 'divider', mt: 0.5, pt: 1 }}>
              {t('accruals:createCustomGroup')}
            </MenuItem>
          </TextField>

          {/* Выбор счёта — появляется автоматически при выборе фиксированной группы */}
          {selectedGroupId && selectedGroupId !== CREATE_CUSTOM_VALUE && osiAccounts.length > 0 && (
            <TextField
              select fullWidth required
              label={t('accruals:selectAccount')}
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(Number(e.target.value))}
              sx={{ mt: 2 }}
            >
              {osiAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.account} ({a.accountTypeNameRu || a.type})
                </MenuItem>
              ))}
            </TextField>
          )}
          {/* OSI-823: кнопка «Добавить» в «Подключить группу» показывается только когда счетов нет; при наличии счетов убрана */}
          {selectedGroupId && selectedGroupId !== CREATE_CUSTOM_VALUE && osiAccounts.length === 0 && (
            <Box sx={{ mt: 2 }}>
              <AddAccountButton addAccountCb={handleAddAccount} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common:cancel')}</Button>
          <Button
            onClick={handleMainAction}
            disabled={
              !selectedGroupId ||
              (selectedGroupId !== CREATE_CUSTOM_VALUE && !selectedAccountId) ||
              activateLoading
            }
          >
            {/* "Далее" только для создания произвольной группы, иначе "Сохранить" */}
            {selectedGroupId === CREATE_CUSTOM_VALUE ? t('common:next') : t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог создания произвольной группы (второй шаг) */}
      <Dialog open={createFormOpen} onClose={() => setCreateFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accruals:createCustomGroup')}</DialogTitle>
        <DialogContent>
          {/* Название (ru) */}
          <TextField
            fullWidth
            required
            label={t('accruals:customGroupName')}
            value={customNameRu}
            onChange={(e) => setCustomNameRu(e.target.value)}
            // Лимит задан GROUP_NAME_MAX_LENGTH (OSI-823, бизнес-лимит; колонка service_groups.name_ru varchar(100));
            // защищает от ложного «успеха» при превышении (бэк молча не сохраняет)
            inputProps={{ maxLength: GROUP_NAME_MAX_LENGTH }}
            // UX-счётчик символов: информирует о приближении к лимиту, не является ошибкой валидации
            helperText={`${customNameRu.length}/${GROUP_NAME_MAX_LENGTH}`}
            // подсветка warning при достижении или превышении лимита (OSI-823: >= — в БД есть старые названия длиннее 70; без error, чтобы border не краснел)
            FormHelperTextProps={customNameRu.length >= GROUP_NAME_MAX_LENGTH ? { sx: { color: 'warning.main' } } : undefined}
            sx={{ mt: 1 }}
          />
          {/* Название (kz) */}
          <TextField
            fullWidth
            label={t('accruals:customGroupNameKz')}
            value={customNameKz}
            onChange={(e) => setCustomNameKz(e.target.value)}
            // Лимит задан GROUP_NAME_MAX_LENGTH (OSI-823, бизнес-лимит; колонка service_groups.name_kz varchar(100));
            // защищает от ложного «успеха» при превышении (бэк молча не сохраняет)
            inputProps={{ maxLength: GROUP_NAME_MAX_LENGTH }}
            // UX-счётчик символов: информирует о приближении к лимиту, не является ошибкой валидации
            helperText={`${customNameKz.length}/${GROUP_NAME_MAX_LENGTH}`}
            // подсветка warning при достижении или превышении лимита (OSI-823: >= — в БД есть старые названия длиннее 70; без error, чтобы border не краснел)
            FormHelperTextProps={customNameKz.length >= GROUP_NAME_MAX_LENGTH ? { sx: { color: 'warning.main' } } : undefined}
            sx={{ mt: 2 }}
          />

          {/* Расчётный счёт — все счета ОСИ */}
          {osiAccounts.length > 0 ? (
            <TextField
              select
              fullWidth
              required
              label={t('accruals:selectAccount')}
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(Number(e.target.value))}
              sx={{ mt: 2 }}
            >
              {osiAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.account} ({a.accountTypeNameRu || a.type})
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography color="text.secondary" variant="body2">{t('accruals:noAccounts')}</Typography>
              {/* Встроенная кнопка добавления счёта — открывает модалку заявки прямо здесь */}
              <AddAccountButton addAccountCb={handleAddAccount} />
            </Box>
          )}

          {/* Категория доходов — из БД (account_report_categories WHERE parent_id=5).
              OSI-806: значение по умолчанию не подставляется — поле открывается пустым,
              поэтому оно помечено required (как «Расчётный счёт») и блокирует «Сохранить» до явного выбора. */}
          <TextField
            select
            fullWidth
            required
            label={t('accruals:reportCategory')}
            value={selectedReportCategoryId}
            // OSI-806: Number('') === 0, а бэк на id=0 бросает «Категория дохода не найдена»
            // (OSI.Core ServiceGroupSvc.cs). Пустую строку обрабатываем явно и храним как '', а не как 0.
            onChange={(e) => {
              const { value } = e.target;
              setSelectedReportCategoryId(value === '' ? '' : Number(value));
            }}
            sx={{ mt: 2 }}
          >
            {reportCategories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.number} — {c.nameRu}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFormOpen(false)}>{t('common:cancel')}</Button>
          {/* OSI-806: категория отчёта добавлена в условие блокировки — без явного выбора
              сохранение недоступно, чтобы на бэк не ушло пустое значение/дефолт 10. */}
          <Button
            onClick={createCustomGroup}
            disabled={!customNameRu.trim() || !selectedAccountId || !selectedReportCategoryId || createLoading}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог смены счёта для существующей группы — текущий счёт жирным и первым.
          Для произвольных групп (editAccountIsCustom = per-OSI, osi_id != null) дополнительно доступно редактирование имени и категории дохода. */}
      <Dialog open={editAccountOpen} onClose={() => setEditAccountOpen(false)} maxWidth="xs" fullWidth>
        {/* Заголовок зависит от типа группы: произвольная — редактирование группы, системная/фиксированная — только смена счёта */}
        <DialogTitle>{editAccountIsCustom ? t('accruals:editGroup') : t('accruals:changeAccount')}</DialogTitle>
        <DialogContent>
          {/* Блок редактирования имени и категории — только для произвольных групп (isCustom === true, osi_id != null).
              Для системных/фиксированных групп попап остаётся прежним (только выбор счёта ниже). */}
          {editAccountIsCustom && (
            <>
              {/* OSI-786: имя группы редактируемо только для ADMIN; для остальных — текущая локаль, disabled.
                  ADMIN → два редактируемых поля (ru/kz), как было до 42a3613b.
                  Не-ADMIN (председатель / доверенный и пр.) → одно disabled-поле в текущей локали (kz → nameKz, иначе nameRu).
                  Стейт customNameRu/customNameKz сохранён — нужен для отображения и для тела PUT (NameRu обязателен на бэке). */}
              {isAdmin ? (
                <>
                  {/* Название (ru) — редактируемое */}
                  <TextField
                    fullWidth
                    required
                    label={t('accruals:customGroupName')}
                    value={customNameRu}
                    onChange={(e) => setCustomNameRu(e.target.value)}
                    inputProps={{ maxLength: GROUP_NAME_MAX_LENGTH }}
                    helperText={`${customNameRu.length}/${GROUP_NAME_MAX_LENGTH}`}
                    FormHelperTextProps={customNameRu.length >= GROUP_NAME_MAX_LENGTH ? { sx: { color: 'warning.main' } } : undefined}
                    sx={{ mt: 1 }}
                  />
                  {/* Название (kz) — редактируемое */}
                  <TextField
                    fullWidth
                    label={t('accruals:customGroupNameKz')}
                    value={customNameKz}
                    onChange={(e) => setCustomNameKz(e.target.value)}
                    inputProps={{ maxLength: GROUP_NAME_MAX_LENGTH }}
                    helperText={`${customNameKz.length}/${GROUP_NAME_MAX_LENGTH}`}
                    FormHelperTextProps={customNameKz.length >= GROUP_NAME_MAX_LENGTH ? { sx: { color: 'warning.main' } } : undefined}
                    sx={{ mt: 2 }}
                  />
                </>
              ) : (
                // Не-ADMIN: имя в текущей локали, только для чтения и как disabled (серое, нередактируемое)
                <TextField
                  fullWidth
                  label={t('accruals:customGroupName')}
                  value={getLocale() === 'kz' ? customNameKz : customNameRu}
                  disabled
                  sx={{ mt: 1 }}
                />
              )}
              {/* Категория доходов.
                  OSI-806: показываем реальную категорию группы; если её нет — поле пустое.
                  required + блокировка «Сохранить» ниже гарантируют, что дефолт 6.7 (id=10) не уедет на бэк. */}
              <TextField
                select
                fullWidth
                required
                label={t('accruals:reportCategory')}
                value={selectedReportCategoryId}
                // OSI-806: Number('') === 0 → бэк отвечает «Категория дохода не найдена». Пустую строку храним как ''.
                onChange={(e) => {
                  const { value } = e.target;
                  setSelectedReportCategoryId(value === '' ? '' : Number(value));
                }}
                sx={{ mt: 2 }}
              >
                {reportCategories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.number} — {c.nameRu}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          {osiAccounts.length > 0 ? (
            <TextField
              select fullWidth required
              label={t('accruals:selectAccount')}
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(Number(e.target.value))}
              sx={{ mt: 1 }}
            >
              {[...osiAccounts]
                .sort((a, b) => {
                  // Текущий привязанный счёт — первым в списке
                  if (a.id === currentLinkedAccountId) return -1;
                  if (b.id === currentLinkedAccountId) return 1;
                  return 0;
                })
                .map((a) => (
                  <MenuItem
                    key={a.id}
                    value={a.id}
                    sx={a.id === currentLinkedAccountId ? { fontWeight: 'bold' } : {}}
                  >
                    {a.account} ({a.accountTypeNameRu || a.type})
                  </MenuItem>
                ))}
            </TextField>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography color="text.secondary">{t('accruals:noAccounts')}</Typography>
            </Box>
          )}
          {/* Кнопка добавления нового счёта — одна, независимо от наличия счетов */}
          <Box sx={{ mt: 1 }}>
            <AddAccountButton addAccountCb={handleAddAccount} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAccountOpen(false)}>{t('common:cancel')}</Button>
          {osiAccounts.length > 0 && (
            // OSI-786: непустое наименование требуем только для ADMIN (он снова может редактировать/очистить имя).
            // Для не-админа имя предзаполнено и не блокирует сохранение.
            // OSI-806: выбранную категорию требуем только для произвольной группы (editAccountIsCustom) и для любой роли —
            // поле показывается всем. Для ФИКСИРОВАННЫХ групп поле категории не рендерится и не уходит в запрос
            // (там отдельный PUT /api/ServiceGroup/account), поэтому блокировать их сохранение нельзя.
            <Button
              onClick={saveEditAccount}
              disabled={
                !selectedAccountId ||
                activateLoading ||
                (editAccountIsCustom && isAdmin && !customNameRu.trim()) ||
                (editAccountIsCustom && !selectedReportCategoryId)
              }
            >
              {t('common:save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
});
